import { cn } from "@gettactic/helpers";
import { PanelLeftOpen, PanelRightOpen } from "lucide-react";
import React from "react";
import { IconButton } from "../../../ui/IconButton";
import { TooltipProvider } from "../../../ui/tooltip";
import { EditorSide, usePanelLayout } from "./EditorLayoutContext";

const SIDEBAR_WIDTH: Record<EditorSide, number> = {
  left: 260,
  right: 264
};
const RAIL_WIDTH = 40;

const EditorSidebar: React.FC<{ side: EditorSide }> = ({ side }) => {
  const { layoutConfig, leftCollapsed, rightCollapsed, setSideCollapsed } =
    usePanelLayout();

  const panels = layoutConfig[side];
  const isCollapsed = side === "left" ? leftCollapsed : rightCollapsed;

  if (panels.length === 0) return null;

  if (isCollapsed) {
    const ExpandIcon = side === "left" ? PanelLeftOpen : PanelRightOpen;
    return (
      <TooltipProvider delayDuration={150}>
        <div
          className={cn(
            "flex flex-none flex-col items-center gap-1 bg-background-primary py-2",
            side === "left"
              ? "border-neutral-200 border-r"
              : "border-neutral-200 border-l"
          )}
          style={{ width: RAIL_WIDTH }}
        >
          {panels.map((panel) => {
            const Icon = panel.icon;
            return (
              <IconButton
                key={panel.id}
                label={`Show ${panel.title}`}
                onClick={() => setSideCollapsed(side, false)}
              >
                <Icon size={16} />
              </IconButton>
            );
          })}
          <div className="flex-1" />
          <IconButton
            label={side === "left" ? "Expand sidebar" : "Expand inspector"}
            onClick={() => setSideCollapsed(side, false)}
          >
            <ExpandIcon size={16} />
          </IconButton>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div
      className={cn(
        "flex min-h-0 flex-none flex-col overflow-hidden bg-background-primary",
        side === "left"
          ? "border-neutral-200 border-r"
          : "border-neutral-200 border-l"
      )}
      style={{ width: SIDEBAR_WIDTH[side] }}
    >
      {panels.map((panel) => {
        const PanelComponent = panel.component;
        return <PanelComponent key={panel.id} side={side} />;
      })}
    </div>
  );
};

/**
 * The editor frame below the top toolbar: fixed-width Layers sidebar on
 * the left, canvas in the middle, fixed-width Inspector on the right.
 * Collapsed sidebars become a 40px icon rail; clicking any rail icon
 * restores the sidebar.
 */
export const EditorLayout: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  return (
    <div className="flex h-full min-h-0 w-full">
      <EditorSidebar side="left" />
      <main className="relative min-w-0 flex-1 bg-white">{children}</main>
      <EditorSidebar side="right" />
    </div>
  );
};
